import { useState, useEffect } from "react";
import axios from "axios";
import { Settings, FileText, MapPin, Send, LogOut, Plus, Trash2, Eye, EyeOff, Download, Upload } from "lucide-react";
import { API } from "../lib/config";

export default function AdminDashboard() {
  const [token, setToken] = useState(localStorage.getItem("qbc_admin_token") || "");
  const [tab, setTab] = useState("settings");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const login = async () => {
    try {
      const r = await axios.post(`${API}/admin/login`, { username, password });
      setToken(r.data.token);
      localStorage.setItem("qbc_admin_token", r.data.token);
      setError("");
    } catch { setError("Invalid credentials"); }
  };

  const logout = () => { setToken(""); localStorage.removeItem("qbc_admin_token"); };
  const headers = { Authorization: `Bearer ${token}` };

  if (!token) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="bg-white rounded-xl border border-slate-200 p-8 w-full max-w-sm" data-testid="admin-login">
        <h1 className="text-xl font-bold text-slate-900 mb-6 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Login</h1>
        <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm mb-3 focus:ring-2 focus:ring-sky-500 outline-none" data-testid="admin-username" />
        <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm mb-3 focus:ring-2 focus:ring-sky-500 outline-none" onKeyDown={e => e.key === "Enter" && login()} data-testid="admin-password" />
        {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
        <button onClick={login} className="w-full bg-sky-700 text-white rounded-xl py-3 text-sm font-medium hover:bg-sky-800" data-testid="admin-login-btn">Login</button>
      </div>
    </div>
  );

  const TABS = [
    { id: "settings", label: "Settings", icon: Settings },
    { id: "blog", label: "Blog Posts", icon: FileText },
    { id: "cities", label: "Cities & Scale", icon: MapPin },
    { id: "indexnow", label: "IndexNow", icon: Send },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Dashboard</h1>
          <button onClick={logout} className="text-sm text-slate-500 hover:text-red-600 flex items-center gap-1" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
        </div>
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? "bg-sky-700 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-sky-300"}`} data-testid={`tab-${t.id}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "settings" && <SettingsTab headers={headers} />}
        {tab === "blog" && <BlogTab headers={headers} />}
        {tab === "cities" && <CitiesTab headers={headers} />}
        {tab === "indexnow" && <IndexNowTab headers={headers} />}
      </div>
    </div>
  );
}

function SettingsTab({ headers }) {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/settings`, { headers }).then(r => setSettings(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    await axios.put(`${API}/admin/settings`, settings, { headers }).catch(() => {});
    setSaving(false);
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="settings-tab">
      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Site Settings</h2>
      {[["site_name", "Site Name"], ["affiliate_url", "Affiliate URL"], ["contact_email", "Contact Email"], ["adsense_code", "AdSense Code"]].map(([key, label]) => (
        <div key={key}>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
          <input value={settings[key] || ""} onChange={e => setSettings({ ...settings, [key]: e.target.value })} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm mt-1 focus:ring-2 focus:ring-sky-500 outline-none" />
        </div>
      ))}
      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold text-slate-500">AdSense Enabled</label>
        <input type="checkbox" checked={settings.adsense_enabled || false} onChange={e => setSettings({ ...settings, adsense_enabled: e.target.checked })} />
      </div>
      <button onClick={save} disabled={saving} className="bg-sky-700 text-white rounded-lg px-6 py-2.5 text-sm font-medium hover:bg-sky-800 disabled:opacity-50" data-testid="settings-save">{saving ? "Saving..." : "Save Settings"}</button>
    </div>
  );
}

function BlogTab({ headers }) {
  const [posts, setPosts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const load = () => axios.get(`${API}/admin/blog/posts`, { headers }).then(r => setPosts(r.data.posts || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const generate = async () => { setGenerating(true); await axios.post(`${API}/admin/blog/generate`, {}, { headers }).catch(() => {}); await load(); setGenerating(false); };
  const generateBatch = async () => { setGenerating(true); await axios.post(`${API}/admin/blog/generate-batch?count=8`, {}, { headers }).catch(() => {}); await load(); setGenerating(false); };
  const togglePublish = async (id, pub) => { await axios.patch(`${API}/admin/blog/posts/${id}`, { published: !pub }, { headers }); load(); };
  const deletePost = async (id) => { if (window.confirm("Delete this post?")) { await axios.delete(`${API}/admin/blog/posts/${id}`, { headers }); load(); } };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="blog-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Blog Posts ({posts.length}/52)</h2>
        <div className="flex gap-2">
          <button onClick={generate} disabled={generating} className="bg-sky-700 text-white rounded-lg px-4 py-2 text-xs font-medium hover:bg-sky-800 disabled:opacity-50 flex items-center gap-1" data-testid="blog-generate"><Plus size={12} /> Generate 1</button>
          <button onClick={generateBatch} disabled={generating} className="bg-emerald-600 text-white rounded-lg px-4 py-2 text-xs font-medium hover:bg-emerald-700 disabled:opacity-50" data-testid="blog-generate-batch">{generating ? "Generating..." : "Generate 8 (Seed)"}</button>
        </div>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {posts.map(p => (
          <div key={p.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
            <div className="flex-1 min-w-0 mr-3">
              <p className="text-sm font-medium text-slate-900 truncate">{p.title}</p>
              <p className="text-xs text-slate-400">{p.created_at?.split("T")[0]} | {p.published ? "Published" : "Draft"}</p>
            </div>
            <div className="flex gap-1">
              <button onClick={() => togglePublish(p.id, p.published)} className="p-1.5 hover:bg-slate-200 rounded" title={p.published ? "Unpublish" : "Publish"}>{p.published ? <Eye size={14} className="text-emerald-600" /> : <EyeOff size={14} className="text-slate-400" />}</button>
              <button onClick={() => deletePost(p.id)} className="p-1.5 hover:bg-red-100 rounded"><Trash2 size={14} className="text-red-500" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CitiesTab({ headers }) {
  const [csvText, setCsvText] = useState("");
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cityCount, setCityCount] = useState(0);

  useEffect(() => { axios.get(`${API}/cities?limit=1`).then(r => setCityCount(r.data.total || 0)).catch(() => {}); }, []);

  const upload = async () => {
    if (!csvText.trim()) return;
    setUploading(true);
    try {
      const r = await axios.post(`${API}/admin/upload-cities`, { csv_data: csvText }, { headers });
      setResult(r.data);
      setCityCount(r.data.total_cities);
    } catch (e) { setResult({ error: e.message }); }
    setUploading(false);
  };

  const loadWave = async (file) => {
    try {
      const r = await fetch(`/csv-waves/${file}`);
      const text = await r.text();
      setCsvText(text);
    } catch { setCsvText("Failed to load wave file"); }
  };

  return (
    <div className="space-y-6" data-testid="cities-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Cities & Scaling</h2>
          <span className="text-sm text-slate-500">Currently <strong>{cityCount}</strong> cities</span>
        </div>
        <div className="mb-4">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Upload Cities CSV</label>
          <p className="text-xs text-slate-400 mb-2">Required: city, state, population. Optional: county, risk_factors</p>
          <textarea value={csvText} onChange={e => setCsvText(e.target.value)} rows={5} placeholder="city,state,population,county,risk_factors&#10;Springfield,IL,114394,Sangamon County,&quot;tornadoes,flooding&quot;" className="w-full px-4 py-3 border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-sky-500 outline-none" data-testid="csv-input" />
        </div>
        <button onClick={upload} disabled={uploading} className="bg-sky-700 text-white rounded-lg px-6 py-2.5 text-sm font-medium hover:bg-sky-800 disabled:opacity-50 flex items-center gap-1" data-testid="csv-upload-btn">
          <Upload size={14} /> {uploading ? "Uploading..." : "Upload & Create Pages"}
        </button>
        {result && !result.error && <p className="text-sm text-emerald-600 mt-3">Added {result.added} cities. Skipped {result.skipped}. Total: {result.total_cities}</p>}
      </div>

      {/* PRE-BUILT WAVES */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-base font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Pre-Built City Waves</h3>
        <p className="text-xs text-slate-400 mb-4">Click a wave to load, then hit "Upload & Create Pages".</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[
            { name: "Wave 2", file: "wave-2-1000-cities.csv", count: "1,000", desc: "Mid-size cities across all 50 states" },
            { name: "Wave 3", file: "wave-3-4000-cities.csv", count: "4,000", desc: "Expanding coverage nationwide" },
            { name: "Wave 4", file: "wave-4-5000-cities.csv", count: "5,000", desc: "Deep regional coverage" },
            { name: "Wave 5", file: "wave-5-10000-cities.csv", count: "10,000", desc: "Comprehensive US coverage" },
            { name: "Wave 6", file: "wave-6-9461-cities.csv", count: "9,461", desc: "Full 30K city coverage" },
          ].map(w => (
            <button key={w.name} onClick={() => loadWave(w.file)} className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-left hover:border-sky-300 transition-all" data-testid={`wave-${w.name.toLowerCase().replace(" ", "-")}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-slate-900">{w.name}</span>
                <span className="text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full font-semibold">{w.count} cities</span>
              </div>
              <p className="text-xs text-slate-500">{w.desc}</p>
              <span className="text-xs text-sky-700 font-medium mt-1 inline-block">Click to load</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function IndexNowTab({ headers }) {
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/indexnow`, {}, { headers });
      setResult(r.data);
    } catch { setResult({ error: "Failed to submit" }); }
    setSubmitting(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="indexnow-tab">
      <h2 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>IndexNow Submission</h2>
      <p className="text-sm text-slate-500 mb-4">Submit all site URLs to Bing, Yahoo, Yandex, and DuckDuckGo for faster indexing.</p>
      <button onClick={submit} disabled={submitting} className="bg-sky-700 text-white rounded-lg px-6 py-2.5 text-sm font-medium hover:bg-sky-800 disabled:opacity-50 flex items-center gap-1" data-testid="indexnow-submit">
        <Send size={14} /> {submitting ? "Submitting..." : "Submit All URLs to IndexNow"}
      </button>
      {result && !result.error && <p className="text-sm text-emerald-600 mt-3">Submitted {result.submitted} URLs. Status: {JSON.stringify(result.results)}</p>}
    </div>
  );
}
